import { computed, runInAction, toJS, observable } from "mobx";
import moment from "moment";
import { compact, toFinite, toString, isEmpty, maxBy } from "lodash";
import { DayTimePeriod, toUpperFirst } from "@bonliva-crm/common";
import locale from "@bonliva-crm/locale";
import { store as rootStore } from "@store";
import {
  OnCallShiftDto,
  PreparednessShiftDto,
  TimereportDayV2Dto,
  TimereportExpenseDtoV2,
  timeReportService
} from "@api/timereports";
import { FormStore } from "@modules/formx";
import { StateMachine } from "@modules/mobx-state-machine";
import { ExtendedPeriodFormData } from "../components/ExtendedPeriodForm";
import { DayInfoData } from "../components/DayInfoForm";
import { TimeReportStatus } from "@graphql";
import { saveDayWarningMessage } from "../save-day-warnings";
import { saveDayErrorSnackbar } from "../save-day-error";

export type TimereportDayFormData = Omit<TimereportDayV2Dto, "mileage"> & {
  mileage: string;
};

export type TimereportProfileState =
  | "loading"
  | "error"
  | "default"
  | "editGeneralInfo"
  | "editOnlyMilesInfo"
  | "editExpenses"
  | "workTimeRanges"
  | "onCallShifts"
  | "preparednessAShifts"
  | "preparednessBShifts"
  | "sickTimeShift"
  | "vabTimeShift";

export class TimereportDayFormStore {
  form: FormStore<TimereportDayFormData>;
  fsm = new StateMachine<TimereportProfileState>("default");

  editOnCallIndex = -1;
  editPreparednessAIndex = -1;
  editPreparednessBIndex = -1;
  editExpensesIndex = -1;

  @observable maxDayShifts = 2;
  @observable open = true;

  constructor(
    public timereportId,
    public initialValues: TimereportDayFormData,
    private fetchStatusHistory,
    public bookingId: string,
    private isOwnedDay: () => boolean = () => true
  ) {
    this.form = new FormStore<TimereportDayFormData>({
      initialValues,
      onSubmit: () => this.submit()
    });
  }

  get stringId() {
    return this.form.values.date;
  }

  /**
   * False when this day belongs to the other side of a month-break week. Staff
   * may still write to it — it is the consultant who would not see the result,
   * whose app reads the day from the booking that owns it — so this only decides
   * whether the day says so.
   */
  @computed get isMonthBreakOwned() {
    return this.isOwnedDay();
  }

  toggleDayOpen = (v) => {
    this.open = v;
  };
  toDefault = () => this.fsm.transition("default");
  toEditGeneralInfo = () => this.fsm.transition("editGeneralInfo");
  toEditMilesInfo = () => this.fsm.transition("editOnlyMilesInfo");
  toEditWorkTime = () => {
    this.fsm.transition("workTimeRanges");
  };
  toEditSickTime = () => {
    this.fsm.transition("sickTimeShift");
  };
  toEditVabTime = () => {
    this.fsm.transition("vabTimeShift");
  };
  toAddOnCall = () => this.toEditOnCall(-1);
  toEditExpenses = (index = -1) => {
    this.editExpensesIndex = index;
    this.fsm.transition("editExpenses");
  };
  toEditOnCall = (index: number) => {
    this.editOnCallIndex = index;
    this.fsm.transition("onCallShifts");
  };
  toAddPreparednessA = () => this.toEditPreparednessA(-1);
  toEditPreparednessA = (index: number) => {
    this.editPreparednessAIndex = index;
    this.fsm.transition("preparednessAShifts");
  };
  toAddPreparednessB = () => this.toEditPreparednessB(-1);
  toEditPreparednessB = (index: number) => {
    this.editPreparednessBIndex = index;
    this.fsm.transition("preparednessBShifts");
  };

  get dateStr() {
    return toUpperFirst(moment.utc(this.initialValues.date).format("dddd DD MMMM"));
  }

  /** The day this form writes, as the calendar date it is read as everywhere else. */
  get isoDate() {
    return moment.utc(this.initialValues.date).format("YYYY-MM-DD");
  }

  @computed get isHaveDayInfo() {
    return !!(
      this.form.values.lunch > 0 ||
      toFinite(this.form.values.mileage) > 0 ||
      this.form.values.comment
    );
  }

  @computed get hasRows() {
    const { values } = this.form;
    return !!(
      !isEmpty(values.preparednessBShifts) ||
      !isEmpty(values.preparednessAShifts) ||
      !isEmpty(values.onCallShifts) ||
      !isEmpty(values.vabTimeShifts) ||
      !isEmpty(values.sickTimeShifts) ||
      !isEmpty(values.workTimeRanges)
    );
  }

  @computed get hideWeekDaysInput() {
    switch (this.fsm.state) {
      case "onCallShifts":
        return this.editOnCallIndex >= 0;
      case "preparednessAShifts":
        return this.editPreparednessAIndex >= 0;
      case "preparednessBShifts":
        return this.editPreparednessBIndex >= 0;
      default:
        return true;
    }
  }

  hasMaxAllowedShifts(name: "workTimeRanges" | "sickTimeShifts" | "vabTimeShifts") {
    return this.form.values[name]?.length >= this.maxDayShifts;
  }

  @computed get isBasicPeriodFormOpen() {
    return this.fsm.is("workTimeRanges");
  }

  @computed get isSickPeriodFormOpen() {
    return this.fsm.is("sickTimeShift");
  }

  @computed get isVabPeriodFormOpen() {
    return this.fsm.is("vabTimeShift");
  }

  get basicFormPeriodData() {
    if (!this.isBasicPeriodFormOpen) {
      return null;
    }

    let data: { values: any; title: string };

    switch (this.fsm.state) {
      case "workTimeRanges": {
        data = {
          values: this.form.values.workTimeRanges,
          title: locale.timeReportType["Common"]
        };
        break;
      }
      case "sickTimeShift": {
        data = {
          values: this.form.values.sickTimeShifts,
          title: locale.timeReportType["SickDay"]
        };
        break;
      }
      case "vabTimeShift": {
        data = {
          values: this.form.values.vabTimeShifts,
          title: locale.timeReportType["VabDay"]
        };
        break;
      }
    }

    if (!data) {
      return null;
    }

    return {
      values: formValueToJS(data.values),
      title: data.title
    };
  }

  setBasicFormData(periods: Array<{ from: string; to: string }>) {
    this.form.values.workTimeRanges = periods;

    this.toDefault();
  }

  setSickFormData(periods: Array<{ from: string; to: string; type: string }>) {
    this.form.values.sickTimeShifts = periods;
    this.toDefault();
  }

  setVabFormData(periods: Array<{ from: string; to: string; type: string }>) {
    this.form.values.vabTimeShifts = periods;

    this.toDefault();
  }

  setBasicFormDataByName(from: string, to: string, name: string) {
    this.form.setValue(name, { from, to });
  }

  get sickFormPeriodData() {
    if (!this.isSickPeriodFormOpen) {
      return null;
    }

    return {
      values: formValueToJS(this.form.values.sickTimeShifts),
      title: locale.timeReportType["SickDay"]
    };
  }

  get vabFormPeriodData() {
    if (!this.isVabPeriodFormOpen) {
      return null;
    }

    return {
      values: formValueToJS(this.form.values.vabTimeShifts),
      title: locale.timeReportType["VabDay"]
    };
  }

  @computed get isExtendedPeriodFormOpen() {
    return this.fsm.is("onCallShifts", "preparednessAShifts", "preparednessBShifts");
  }

  get extendedPeriodFormData() {
    if (!this.isExtendedPeriodFormOpen) {
      return null;
    }

    let values: ExtendedPeriodFormData;

    switch (this.fsm.state) {
      case "onCallShifts":
        // eslint-disable-next-line no-case-declarations
        const onCallShift = this.form.values.onCallShifts[this.editOnCallIndex];
        values = onCallShift
          ? {
              from: onCallShift.period?.from || "",
              to: onCallShift.period?.to,
              periods: onCallShift.passivePeriods || []
            }
          : null;
        return {
          values,
          title: locale.OnCall,
          periodsTitle: locale.PassivePeriods
        };
      case "preparednessAShifts":
        // eslint-disable-next-line no-case-declarations
        const prepAShift =
          this.form.values.preparednessAShifts[this.editPreparednessAIndex];
        values = prepAShift
          ? {
              from: prepAShift.period?.from || "",
              to: prepAShift.period?.to,
              periods: prepAShift.activePeriods || []
            }
          : null;
        return {
          values,
          title: locale.PreparednessA,
          periodsTitle: locale.ActivePeriods
        };
      case "preparednessBShifts":
        // eslint-disable-next-line no-case-declarations
        const prepBShift =
          this.form.values.preparednessBShifts[this.editPreparednessBIndex];
        values = prepBShift
          ? {
              from: prepBShift.period?.from || "",
              to: prepBShift.period?.to,
              periods: prepBShift.activePeriods || []
            }
          : null;
        return {
          values: formValueToJS(values),
          title: locale.PreparednessB,
          periodsTitle: locale.ActivePeriods
        };
    }
  }

  setExtendedFromData(
    from: string,
    to: string,
    periods: Array<{ from: string; to: string }>
  ) {
    if (!this.isExtendedPeriodFormOpen) {
      return;
    }

    switch (this.fsm.state) {
      case "onCallShifts":
        // eslint-disable-next-line no-case-declarations
        const o1: OnCallShiftDto = {
          period: { from, to },
          passivePeriods: periods
        };
        this.form.setValue(
          "onCallShifts",
          replaceOrPush(this.form.values.onCallShifts, o1, this.editOnCallIndex)
        );
        break;

      case "preparednessAShifts":
        // eslint-disable-next-line no-case-declarations
        const o2: PreparednessShiftDto = {
          period: { from, to },
          activePeriods: periods
        };
        this.form.setValue(
          "preparednessAShifts",
          replaceOrPush(
            this.form.values.preparednessAShifts,
            o2,
            this.editPreparednessAIndex
          )
        );

        break;

      case "preparednessBShifts":
        // eslint-disable-next-line no-case-declarations
        const o3: PreparednessShiftDto = {
          period: { from, to },
          activePeriods: periods
        };
        this.form.setValue(
          "preparednessBShifts",
          replaceOrPush(
            this.form.values.preparednessBShifts,
            o3,
            this.editPreparednessAIndex
          )
        );
        break;
    }

    this.toDefault();
  }

  setExtendedFromDataByName(
    from: string,
    to: string,
    periods: Array<{ from: string; to: string }>,
    name: TimereportProfileState
  ) {
    switch (name) {
      case "onCallShifts":
        // eslint-disable-next-line no-case-declarations
        const o1: OnCallShiftDto = {
          period: { from, to },
          passivePeriods: periods
        };
        this.form.push("onCallShifts", o1);
        break;

      case "preparednessAShifts":
        // eslint-disable-next-line no-case-declarations
        const o2: PreparednessShiftDto = {
          period: { from, to },
          activePeriods: periods
        };
        this.form.push("preparednessAShifts", o2);

        break;

      case "preparednessBShifts":
        // eslint-disable-next-line no-case-declarations
        const o3: PreparednessShiftDto = {
          period: { from, to },
          activePeriods: periods
        };
        this.form.push("preparednessBShifts", o3);
        break;
    }
  }

  setGeneralInfo = (values: DayInfoData) => {
    runInAction(() => {
      this.form.values.lunch = toFinite(toString(values.lunch).replace(",", "."));
      this.form.values.mileage = values.miles;
      this.form.values.comment = values.comment;

      this.form.validate();
      this.toDefault();
    });
  };

  setExpenses = (values: TimereportExpenseDtoV2) => {
    runInAction(() => {
      this.form.setValue(
        "expenses",
        replaceOrPush(this.form.values.expenses, values, this.editExpensesIndex)
      );

      this.form.validate();
      this.toDefault();
    });
  };

  get expensesToEdit(): TimereportExpenseDtoV2 {
    const item = this.form.values.expenses?.find((x, i) => i === this.editExpensesIndex);
    return (
      item ?? {
        attachments: [],
        comment: "",
        amount: null
      }
    );
  }

  remExpenses = (i: number) => {
    this.form.removeByIndex("expenses", i);
  };

  deleteWorkTime = () => {
    this.form.setValue("workTimeRanges", []);
  };

  deleteSickTime = () => {
    this.form.setValue("sickTimeShifts", []);
  };

  deleteVabTime = () => {
    this.form.setValue("vabTimeShifts", []);
  };

  deleteOnCall = (i: number) => {
    this.form.removeByIndex("onCallShifts", i);
  };

  deletePrepA = (i: number) => {
    this.form.removeByIndex("preparednessAShifts", i);
  };

  deletePrepB = (i: number) => {
    this.form.removeByIndex("preparednessBShifts", i);
  };

  getMaxRestAllowedRange = () => {
    const allowedPeriod = DayTimePeriod.Parse("07:00", "19:00");

    const ranges: Array<{ from: string; to: string }> = [];
    ranges.push(...this.form.values.workTimeRanges);
    ranges.push(...this.form.values.sickTimeShifts.filter((x) => x.type === "Work"));

    const dayTimePeriods = compact(ranges)
      .map((x) => DayTimePeriod.Parse(x.from, x.to))
      .filter((x) => x.overlaps(allowedPeriod))
      .map((x) => x.clamp(allowedPeriod));

    const maxPeriod = maxBy(dayTimePeriods, (x) => x.durationMinutes());

    if (!maxPeriod) {
      return null;
    }

    return {
      from: maxPeriod.from.toString(),
      to: maxPeriod.to.toString()
    };
  };

  isLunchTimeDisabled = () => {
    if (this.form.values.lunch) {
      return false;
    }

    const maxRange = this.getMaxRestAllowedRange();

    if (!maxRange) {
      return false;
    }

    const dayTimePeriod = DayTimePeriod.Parse(maxRange.from, maxRange.to);
    const allowedPeriod = DayTimePeriod.Parse("07:00", "19:00");

    if (!maxRange?.from || !maxRange?.to) {
      return true;
    }

    return !allowedPeriod.overlaps(dayTimePeriod);
  };

  lunchTimeMax = () => {
    const maxRange = this.getMaxRestAllowedRange();

    if (!maxRange) {
      return 0;
    }

    const dayTimePeriod = DayTimePeriod.Parse(maxRange.from, maxRange.to);
    const allowedPeriod = DayTimePeriod.Parse("07:00", "19:00");

    if (!dayTimePeriod.overlaps(allowedPeriod)) {
      return 0;
    }

    const clamped = dayTimePeriod.clamp(allowedPeriod);

    return clamped.durationMinutes();
  };

  submit = async () => {
    const dto = cleanUpDto(toJS(this.form.values));

    return timeReportService
      .saveDay({
        timereportId: this.timereportId,
        timereportDayV2Dto: dto
      })
      .then((result) => {
        const timereport = result.data.timereport;
        const newValues = timereport.days.find(
          (x) => x.date.valueOf() === this.form.values.date.valueOf()
        );

        if (!newValues) {
          throw new Error("cannot find timereport day");
        }

        const initial = {
          ...newValues,
          mileage: toString(newValues.mileage).replace(".", ",")
        };

        this.form.reset(initial, true);
        rootStore().snackbars.success(locale.Saved);

        // The rules CRM is exempt from (hours a duplicate booking already holds, a day the other
        // side of a month-break week owns) come back as warnings on a save that went through.
        // They describe a week that is now wrong in a way nothing downstream catches, so they
        // get the long duration the refusal used to have.
        const warning = saveDayWarningMessage(result.data.warnings);

        if (warning) {
          rootStore().snackbars.warning({ text: warning, duration: 15000 });
        }

        rootStore().localPubSub.timereportUpdated.trigger({
          timereportId: timereport.timereportId,
          status: timereport.status
        });
        this.fetchStatusHistory();
        // Remove booking week from search result
        const kitFlowSearch = (window as any)?.bookingKitSupportSearchStore;
        const kitFlowFilter = (window as any)?.bookingKitSupportSearchStore
          ?.filterManager;

        if (kitFlowSearch && kitFlowFilter) {
          const timeReportStatus = kitFlowFilter.filter?.timeReportStatus;
          if (
            timeReportStatus.length > 0 &&
            !timeReportStatus.includes(timereport.status)
          ) {
            kitFlowSearch.deleteRowByBookingId(this.bookingId);
          }
          kitFlowSearch.updateRowTimeReportStatusByBookingId(
            this.bookingId,
            timereport.status as TimeReportStatus
          );
        }
      })
      .catch((e) => {
        rootStore().snackbars.error(saveDayErrorSnackbar(e));
      });
  };
}

function formValueToJS(obj: any) {
  return toJS(Array.isArray(obj) ? obj.map((x) => toJS(x)) : obj);
}

function replaceOrPush<T>(arr: T[], value: T, index: number) {
  if (arr[index]) {
    arr[index] = value;
  } else {
    arr.push(value);
  }

  return arr;
}

function cleanUpDto(dto: TimereportDayFormData): TimereportDayV2Dto {
  return {
    ...dto,
    comment: toString(dto.comment),
    expenses: dto.expenses.map((x) => ({
      ...x,
      attachments: x.attachments.map((a) => ({
        ...a,
        previewUri: a.previewUri ?? ""
      }))
    })),
    mileage: toFinite(toString(dto.mileage).replace(",", "."))
  };
}
